import { CommonModule } from "@angular/common";
import { Component, computed, inject, signal } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { ColDef } from "ag-grid-community";
import { UiLibraryModule } from "@template-ui/template-ui.module";
import { ExcelService } from "@template-ui/services/excel-export.service";

import { AppBaseComponent } from "@app/shared/components/app-base.component";
import { createTerminologyUsageSignals } from "@app/shared/terminology/terminology-usage-signals";
import { ButtonCellRenderer } from "@app/shared/components/tables/ag-grid-cells/button-cell-renderer/button-cell-renderer.component";
import { PhmsIntakeRuntimeService } from "../phms-intake-runtime.service";
import {
    PhmsIntakeDisplayError,
    PhmsIntakeStagingBatchDto,
    PhmsIntakeStagingParsedRow,
    PhmsIntakeStagingRowDto,
    PhmsIntakeStagingUploadRequest,
    mapPhmsIntakeError,
} from "./phms-intake-staging.models";
import { PhmsIntakeStagingService } from "./phms-intake-staging.service";
import { PHMS_OPERATIONAL_TERMINOLOGY } from "../../phms-operational.terminology";

const PHMS_INTAKE_SUBMIT = "phms.intake.submit";

@Component({
    selector: "phms-intake-staging",
    standalone: true,
    imports: [CommonModule, UiLibraryModule],
    templateUrl: "./phms-intake-staging.component.html",
    styleUrl: "./phms-intake-staging.component.scss",
})
export class PhmsIntakeStagingComponent extends AppBaseComponent {
    readonly operationalText = createTerminologyUsageSignals(PHMS_OPERATIONAL_TERMINOLOGY.intakeStaging);
    private readonly runtime = inject(PhmsIntakeRuntimeService);
    private readonly stagingService = inject(PhmsIntakeStagingService);
    private readonly excelService = inject(ExcelService);

    readonly canSubmit = this.roles.hasAnyPermissionSignal(PHMS_INTAKE_SUBMIT);

    readonly isUploading = signal(false);
    readonly isApplying = signal(false);
    readonly savingRowId = signal<number | null>(null);
    readonly programScopeKeysLoaded = signal(false);
    readonly programScopeKey = signal("");
    readonly selectedFileName = signal<string | null>(null);
    readonly batch = signal<PhmsIntakeStagingBatchDto | null>(null);
    readonly applyResult = signal<PhmsIntakeStagingApplyResultView | null>(null);
    readonly successMessage = signal<string | null>(null);
    readonly errorState = signal<PhmsIntakeDisplayError | null>(null);

    private parsedRows: PhmsIntakeStagingParsedRow[] = [];

    readonly nameLabel = this.runtime.incarceratedPersonDisplayNameLabel;
    readonly identifierLabel = this.runtime.incarceratedPersonIdentifierLabel;
    readonly programScopeKeyLabel = this.runtime.programScopeKeyLabel;
    readonly programScopeKeyOptions = this.runtime.programScopeKeyOptions;

    readonly hasProgramScopeKeyOptions = computed(() => this.programScopeKeyOptions().length > 0);
    readonly hasBatch = computed(() => this.batch() !== null);
    readonly canApply = computed(() => {
        const current = this.batch();
        return current !== null && current.validRowCount > 0 && current.batchStatusCode !== "applied";
    });
    readonly hasExcludedRows = computed(() => (this.applyResult()?.excludedRows.length ?? 0) > 0);

    /** Editable copies of the batch rows; ag-grid mutates these on inline edit, not the batch signal. */
    readonly editableRows = computed<PhmsIntakeStagingRowDto[]>(() =>
        (this.batch()?.rows ?? []).map((row) => ({ ...row })),
    );

    /**
     * Column definitions for the inline-editable correction grid. The three identity fields are
     * editable; the trailing column renders a per-row "Save" button wired to {@link saveRow}.
     */
    readonly correctionColumnDefs = computed<ColDef[]>(() => [
        { field: "rowNumber", headerName: "#", width: 80, sortable: true, editable: false },
        { field: "incarceratedPersonDisplayName", headerName: this.nameLabel(), flex: 1, minWidth: 160, editable: true },
        { field: "incarceratedPersonIdentifier", headerName: this.identifierLabel(), flex: 1, minWidth: 140, editable: true },
        { field: "programScopeKey", headerName: this.programScopeKeyLabel(), flex: 1, minWidth: 140, editable: true },
        { field: "rowStatusCode", headerName: this.operationalText.status(), width: 120, editable: false },
        {
            headerName: "Validation",
            flex: 2,
            minWidth: 200,
            sortable: false,
            editable: false,
            valueGetter: ({ data }) => (data as PhmsIntakeStagingRowDto).validationErrors.join("; "),
        },
        {
            headerName: "Correct",
            width: 110,
            maxWidth: 120,
            sortable: false,
            editable: false,
            cellRenderer: ButtonCellRenderer,
            cellRendererParams: {
                label: "Save",
                onClick: (params: { rowData: PhmsIntakeStagingRowDto }) => this.saveRow(params.rowData),
                enabled: (params: { rowData: PhmsIntakeStagingRowDto }) => this.canSaveRow(params.rowData),
            },
        },
    ]);

    /** Column definitions for the read-only excluded-rows grid; headers track the runtime labels. */
    readonly excludedRowColumnDefs = computed<ColDef[]>(() => [
        { field: "rowNumber", headerName: "#", width: 80, sortable: true },
        { field: "incarceratedPersonDisplayName", headerName: this.nameLabel(), flex: 1, minWidth: 160, sortable: true },
        { field: "incarceratedPersonIdentifier", headerName: this.identifierLabel(), flex: 1, minWidth: 140, sortable: true },
        { field: "programScopeKey", headerName: this.programScopeKeyLabel(), flex: 1, minWidth: 140, sortable: true },
        {
            headerName: "Validation",
            flex: 2,
            minWidth: 200,
            sortable: false,
            valueGetter: ({ data }) => (data as PhmsIntakeStagingRowDto).validationErrors.join("; "),
        },
    ]);

    readonly pageTitle = "PHMS Bulk Intake Staging";
    readonly pageSubtitle =
        "Upload a CSV of intake rows to stage, validate, correct, and apply in bulk. Server-side authorization remains authoritative.";

    constructor() {
        super();
        this.runtime
            .loadProgramScopeKeyOptions()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: () => this.programScopeKeysLoaded.set(true),
                error: () => this.programScopeKeysLoaded.set(true),
            });

        this.runtime.loadFieldLabels().pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
    }

    onProgramScopeKeyChange(value: string): void {
        this.programScopeKey.set(value);
    }

    onFileSelected(event: Event): void {
        this.clearMessages();
        const input = event.target as HTMLInputElement;
        const file = input.files?.[0];
        if (!file) {
            this.selectedFileName.set(null);
            this.parsedRows = [];
            return;
        }

        this.selectedFileName.set(file.name);
        const reader = new FileReader();
        reader.onload = () => {
            try {
                this.parsedRows = parseCsv(typeof reader.result === "string" ? reader.result : "");
                if (this.parsedRows.length === 0) {
                    this.errorState.set({
                        title: "No rows found",
                        messages: ["The selected file did not contain any data rows."],
                        issueCodes: [],
                    });
                }
            } catch {
                this.parsedRows = [];
                this.errorState.set({
                    title: "File could not be parsed",
                    messages: ["The selected file could not be read as CSV. Verify the header row and try again."],
                    issueCodes: [],
                });
            }
        };
        reader.onerror = () => {
            this.parsedRows = [];
            this.errorState.set({
                title: "File could not be read",
                messages: ["The selected file could not be read. Try again or choose a different file."],
                issueCodes: [],
            });
        };
        reader.readAsText(file);
    }

    upload(): void {
        if (!this.guardPermission()) {
            return;
        }

        this.clearMessages();

        const scopeKey = this.programScopeKey().trim();
        if (!scopeKey) {
            this.errorState.set({
                title: "Program scope required",
                messages: ["Select a program scope before uploading."],
                issueCodes: [],
            });
            return;
        }

        if (this.parsedRows.length === 0) {
            this.errorState.set({
                title: "No rows to upload",
                messages: ["Choose a CSV file with at least one data row before uploading."],
                issueCodes: [],
            });
            return;
        }

        const request: PhmsIntakeStagingUploadRequest = {
            programScopeKey: scopeKey,
            sourceFileName: this.selectedFileName() ?? "upload.csv",
            rows: this.parsedRows.map((row) => ({
                incarceratedPersonDisplayName: row.incarceratedPersonDisplayName,
                incarceratedPersonIdentifier: row.incarceratedPersonIdentifier,
                programScopeKey: row.programScopeKey || scopeKey,
            })),
        };

        this.isUploading.set(true);
        this.stagingService
            .uploadBatch(request)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (result) => {
                    this.isUploading.set(false);
                    this.setBatch(result);
                    this.successMessage.set(`Uploaded ${result.rowCount} row(s). Review validation results below.`);
                },
                error: (error: unknown) => {
                    this.isUploading.set(false);
                    this.errorState.set(mapPhmsIntakeError(error));
                },
            });
    }

    validate(): void {
        const current = this.batch();
        if (!current || !this.guardPermission()) {
            return;
        }

        this.clearMessages();
        this.stagingService
            .validateBatch(current.id)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (result) => {
                    this.setBatch(result);
                    this.successMessage.set(
                        `Validation complete: ${result.validRowCount} valid, ${result.invalidRowCount} invalid.`,
                    );
                },
                error: (error: unknown) => this.errorState.set(mapPhmsIntakeError(error)),
            });
    }

    /** Whether the per-row Save button is actionable: nothing else saving and the row isn't promoted. */
    private canSaveRow(row: PhmsIntakeStagingRowDto): boolean {
        return this.savingRowId() === null && row.rowStatusCode !== "promoted";
    }

    /** Persist the inline-edited values for a single grid row (ag-grid mutates the passed row in place). */
    saveRow(row: PhmsIntakeStagingRowDto): void {
        if (!this.guardPermission()) {
            return;
        }

        const current = this.batch();
        if (!current) {
            return;
        }

        this.clearMessages();
        this.savingRowId.set(row.id);
        this.stagingService
            .correctRow(current.id, row.id, {
                incarceratedPersonDisplayName: row.incarceratedPersonDisplayName,
                incarceratedPersonIdentifier: row.incarceratedPersonIdentifier,
                programScopeKey: row.programScopeKey,
            })
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (result) => {
                    this.savingRowId.set(null);
                    this.setBatch(result);
                    this.successMessage.set(`Row ${row.rowNumber} updated and re-validated.`);
                },
                error: (error: unknown) => {
                    this.savingRowId.set(null);
                    this.errorState.set(mapPhmsIntakeError(error));
                },
            });
    }

    apply(): void {
        const current = this.batch();
        if (!current || !this.guardPermission()) {
            return;
        }

        this.clearMessages();
        this.isApplying.set(true);
        this.stagingService
            .applyBatch(current.id)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (result) => {
                    this.isApplying.set(false);
                    this.applyResult.set({
                        appliedCount: result.appliedCount,
                        excludedCount: result.excludedCount,
                        excludedRows: result.excludedRows,
                    });
                    this.successMessage.set(
                        `Applied ${result.appliedCount} row(s); ${result.excludedCount} excluded.`,
                    );
                    this.stagingService
                        .getBatch(current.id)
                        .pipe(takeUntilDestroyed(this.destroyRef))
                        .subscribe({
                            next: (refreshed) => this.setBatch(refreshed),
                            error: () => undefined,
                        });
                },
                error: (error: unknown) => {
                    this.isApplying.set(false);
                    this.errorState.set(mapPhmsIntakeError(error));
                },
            });
    }

    exportExcludedRows(): void {
        const result = this.applyResult();
        if (!result || result.excludedRows.length === 0) {
            return;
        }

        const report = result.excludedRows.map((row) => ({
            "Row Number": row.rowNumber,
            "Status": row.rowStatusCode,
            [this.nameLabel()]: row.incarceratedPersonDisplayName,
            [this.identifierLabel()]: row.incarceratedPersonIdentifier,
            [this.programScopeKeyLabel()]: row.programScopeKey,
            "Validation Errors": row.validationErrors.join("; "),
        }));

        void this.excelService.exportAsExcelFile(report, "phms-intake-excluded-rows");
    }

    private setBatch(batch: PhmsIntakeStagingBatchDto): void {
        this.batch.set(batch);
    }

    private guardPermission(): boolean {
        if (this.canSubmit()) {
            return true;
        }

        this.errorState.set({
            title: "Access denied",
            messages: ["You do not have permission to submit intake submissions."],
            issueCodes: [],
        });
        return false;
    }

    private clearMessages(): void {
        this.successMessage.set(null);
        this.errorState.set(null);
    }
}

interface PhmsIntakeStagingApplyResultView {
    appliedCount: number;
    excludedCount: number;
    excludedRows: PhmsIntakeStagingRowDto[];
}

const HEADER_ALIASES: Record<string, keyof PhmsIntakeStagingParsedRow> = {
    incarceratedpersondisplayname: "incarceratedPersonDisplayName",
    displayname: "incarceratedPersonDisplayName",
    name: "incarceratedPersonDisplayName",
    incarceratedpersonidentifier: "incarceratedPersonIdentifier",
    identifier: "incarceratedPersonIdentifier",
    id: "incarceratedPersonIdentifier",
    programscopekey: "programScopeKey",
    programscope: "programScopeKey",
    scope: "programScopeKey",
};

export function parseCsv(content: string): PhmsIntakeStagingParsedRow[] {
    const lines = content
        .split(/\r\n|\r|\n/)
        .map((line) => line.trim())
        .filter((line) => line.length > 0);

    if (lines.length === 0) {
        return [];
    }

    const headerCells = splitCsvLine(lines[0]).map((cell) =>
        cell.toLowerCase().replace(/[^a-z0-9]/g, ""),
    );
    const columnMap = headerCells.map((cell) => HEADER_ALIASES[cell]);

    return lines.slice(1).map((line) => {
        const cells = splitCsvLine(line);
        const row: PhmsIntakeStagingParsedRow = {
            incarceratedPersonDisplayName: "",
            incarceratedPersonIdentifier: "",
            programScopeKey: "",
        };
        columnMap.forEach((field, index) => {
            if (field) {
                row[field] = (cells[index] ?? "").trim();
            }
        });
        return row;
    });
}

function splitCsvLine(line: string): string[] {
    const cells: string[] = [];
    let current = "";
    let inQuotes = false;

    for (let index = 0; index < line.length; index += 1) {
        const char = line[index];
        if (char === '"') {
            if (inQuotes && line[index + 1] === '"') {
                current += '"';
                index += 1;
            } else {
                inQuotes = !inQuotes;
            }
        } else if (char === "," && !inQuotes) {
            cells.push(current);
            current = "";
        } else {
            current += char;
        }
    }
    cells.push(current);
    return cells;
}
